import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';

import { useAuth } from 'contexts/AuthContext';

import RequestStatusChip from 'components/chip/RequestStatusChip';
import RequestTypeChip from 'components/chip/RequestTypeChip';
import Circular from 'components/common/CustomCircular';
import { Condition, Filter } from 'components/condition-list/SearchFilter';
import {
  ConsoleColumn,
  ConsoleDetailDrawer,
  ConsoleHeader,
  ConsoleSearchFilter,
  ConsoleTable,
  FormToc,
  useQueryInitialFilters,
} from 'components/console';
import { UserSession } from 'stores/AuthenticationStoreAdapter';
import { ClientResult, listClient } from 'stores/ClientStoreAdapter';
import {
  ContractRequestDetailResult,
  ContractRequestListResult,
  getContractRequest,
  listContractRequest,
} from 'stores/ContractRequestStoreAdapter';
import { MemberListResult, listMember } from 'stores/MemberStoreAdapter';
import { PartnerResult, listPartner } from 'stores/PartnerStoreAdapter';
import { ProjectListResult, listProject } from 'stores/ProjectStoreAdapter';
import { UserResult } from 'stores/UserStoreAdapter';
import { generateUUID } from 'utils/Common';
import { formatDate } from 'utils/DateTimeFormatter';
import { errorDefault } from 'utils/Toastify';

import ChecklistSection from './components/ChecklistSection';
import CommentSection from './components/CommentSection';
import ContractRequestDetail from './components/ContractRequestDetail';

const ContractRequestManagement: React.FC = () => {
  const navigate = useNavigate();
  const { userSession, users, managers } = useAuth();
  const { contractRequestId: urlContractRequestId } = useParams<{
    contractRequestId?: string;
  }>();
  const [isProgress, setProgress] = useState(true);
  const [requests, setRequests] = useState<ContractRequestListResult[]>([]);
  const [selectedDetail, setSelectedDetail] = useState<ContractRequestDetailResult | null>(null);
  const [directDetail, setDirectDetail] = useState<ContractRequestDetailResult | null>(null);
  // 現在適用中の検索パラメータ（更新後・再読み込みで同条件を再検索するため保持）
  const [activeParams, setActiveParams] = useState<Record<string, string | boolean | undefined>>(
    {},
  );

  // 検索条件用マスタデータ
  const [members, setMembers] = useState<MemberListResult[]>([]);
  const [partners, setPartners] = useState<PartnerResult[]>([]);
  const [clients, setClients] = useState<ClientResult[]>([]);
  const [projects, setProjects] = useState<ProjectListResult[]>([]);

  const isBackOffice = userSession.roles.includes('BACK_OFFICE');

  // デフォルトフィルター: 管理部門の場合は requestStatus = SUBMITTED
  const defaultFilters: Filter[] = useMemo(() => {
    if (isBackOffice) {
      return [
        {
          id: generateUUID(),
          key: 'requestStatus',
          color: '#9DC08B',
          value: { key: 'SUBMITTED', name: '申請中' },
        },
      ];
    }
    return [];
  }, [isBackOffice]);

  useEffect(() => {
    listMember({})
      .then(setMembers)
      .catch(() => {});
    listPartner({})
      .then(setPartners)
      .catch(() => {});
    listClient({})
      .then(setClients)
      .catch(() => {});
    listProject({})
      .then(setProjects)
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (urlContractRequestId) {
      setProgress(true);
      getContractRequest({ contractRequestId: urlContractRequestId })
        .then((detail) => {
          setDirectDetail(detail);
          setProgress(false);
        })
        .catch(() => {
          setProgress(false);
          errorDefault('契約申請詳細の取得に失敗しました');
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlContractRequestId]);

  const fetchRequests = (params: Record<string, string | boolean | undefined>) => {
    setProgress(true);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    listContractRequest(params as any)
      .then((result) => {
        setRequests(result);
        setProgress(false);
      })
      .catch(() => {
        setProgress(false);
        errorDefault('契約申請の取得に失敗しました');
      });
  };

  // 現在適用中の検索条件で再検索する
  const refetch = () => fetchRequests(activeParams);

  const handleSearch = (filters: Filter[]) => {
    setSelectedDetail(null);
    const params: Record<string, string> = {};
    filters.forEach((f) => {
      params[f.key] = f.value.key;
    });
    setActiveParams(params);
    fetchRequests(params);
  };

  const handleRowClick = (row: ContractRequestListResult) => {
    setProgress(true);
    getContractRequest({ contractRequestId: row.contractRequestId })
      .then((detail) => {
        setSelectedDetail(detail);
        setProgress(false);
      })
      .catch(() => {
        setProgress(false);
        errorDefault('契約申請詳細の取得に失敗しました');
      });
  };

  // 更新後の挙動:
  // - status（対応開始/承認/否決/取消）: ドロワーは閉じず、詳細を最新化しつつ一覧も再取得
  // - content（保存/申請）: ドロワーを閉じて現在の検索条件で再検索
  const handleDetailUpdated = (kind: 'status' | 'content' = 'content') => {
    if (kind === 'status') {
      if (selectedDetail) {
        getContractRequest({ contractRequestId: selectedDetail.contractRequestId })
          .then((detail) => setSelectedDetail(detail))
          .catch(() => errorDefault('詳細の再取得に失敗しました'));
      }
      refetch();
      return;
    }
    setSelectedDetail(null);
    refetch();
  };

  const searchConditions: Condition[] = useMemo(
    () => [
      { key: 'managementId', name: '管理ID', type: 'text' as const, color: '#78C1F3' },
      {
        key: 'requestType',
        name: '申請種別',
        type: 'select' as const,
        color: '#B799FF',
        values: [
          { key: 'CREATE', name: '新規申請' },
          { key: 'CHANGE', name: '変更申請' },
          { key: 'TERMINATE', name: '解除申請' },
        ],
      },
      {
        key: 'requestStatus',
        name: 'ステータス',
        type: 'select' as const,
        color: '#9DC08B',
        values: [
          { key: 'ADJUSTING', name: '調整中' },
          { key: 'SUBMITTED', name: '申請中' },
          { key: 'IN_PROGRESS', name: '対応中' },
          { key: 'APPROVED', name: '承認' },
          { key: 'REJECTED', name: '否決' },
          { key: 'CANCELLED', name: '取消' },
        ],
      },
      {
        key: 'personInChargeId',
        name: '担当者',
        type: 'user-select-single' as const,
        color: '#F7C8E0',
        users,
      },
      {
        key: 'managerId',
        name: '課長',
        type: 'manager-select-single' as const,
        color: '#66BB6A',
        managers: managers as unknown as UserResult[],
      },
      {
        key: 'projectId',
        name: 'プロジェクト',
        type: 'project-select-single' as const,
        color: '#FFB74D',
        projects,
      },
      {
        key: 'memberId',
        name: 'メンバー',
        type: 'member-select-single' as const,
        color: '#4FC3F7',
        members,
      },
      {
        key: 'partnerId',
        name: 'パートナー',
        type: 'partner-select-single' as const,
        color: '#CE93D8',
        partners,
      },
      {
        key: 'clientId',
        name: '取引先',
        type: 'client-select-single' as const,
        color: '#A5D6A7',
        clients,
      },
      {
        key: 'approvedBy',
        name: '承認者',
        type: 'user-select-single' as const,
        color: '#BCAAA4',
        users,
      },
      {
        key: 'submittedFrom',
        name: '申請日(From)',
        type: 'date-time-picker-start' as const,
        color: '#81D4FA',
      },
      {
        key: 'submittedTo',
        name: '申請日(To)',
        type: 'date-time-picker-end' as const,
        color: '#81D4FA',
      },
      {
        key: 'approvedFrom',
        name: '承認日(From)',
        type: 'date-time-picker-start' as const,
        color: '#C5E1A5',
      },
      {
        key: 'approvedTo',
        name: '承認日(To)',
        type: 'date-time-picker-end' as const,
        color: '#C5E1A5',
      },
      {
        key: 'needsOriginSheet',
        name: '原籍確認シート必要',
        type: 'check' as const,
        color: '#78909C',
        value: { key: 'true', name: '原籍確認シート必要' },
      },
    ],
    [users, members, partners, clients, projects, managers],
  );

  // URL クエリパラメータを初期検索条件として反映（担当者/課長/承認者の self 解決対応）
  const { queryParams, initialFilters: queryFilters } = useQueryInitialFilters(searchConditions, {
    selfUserId: userSession.userId,
    selfKeys: ['personInChargeId', 'managerId', 'approvedBy'],
  });
  // 検索バー表示用: BACK_OFFICE デフォルト ＋ クエリ由来（クエリ優先）
  const initialFilters: Filter[] = useMemo(() => {
    const queryKeys = new Set(queryFilters.map((f) => f.key));
    const base = defaultFilters.filter((f) => !queryKeys.has(f.key));
    return [...base, ...queryFilters];
  }, [defaultFilters, queryFilters]);

  // 一覧の取得（ID指定モードでは行わない）: デフォルト ＋ クエリ（クエリ優先）
  useEffect(() => {
    if (urlContractRequestId) return;
    const params: Record<string, string | boolean | undefined> = {};
    defaultFilters.forEach((f) => {
      params[f.key] = f.value.key;
    });
    Object.assign(params, queryParams);
    setActiveParams(params);
    fetchRequests(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams, urlContractRequestId]);

  const columns: ConsoleColumn<ContractRequestListResult>[] = [
    {
      key: 'managementId',
      header: '管理ID',
      width: 96,
      render: (r) => (
        <Box component="span" sx={{ fontWeight: 600 }}>
          {r.managementId}
        </Box>
      ),
    },
    {
      key: 'requestType',
      header: '種別',
      width: 84,
      render: (r) => <RequestTypeChip requestType={r.requestType} />,
    },
    {
      key: 'requestStatus',
      header: 'ステータス',
      width: 88,
      render: (r) => <RequestStatusChip requestStatus={r.requestStatus} />,
    },
    {
      key: 'projectName',
      header: 'プロジェクト',
      width: 200,
      ellipsis: (r) => r.projectName,
    },
    {
      key: 'memberName',
      header: '要員 / パートナー',
      width: 180,
      ellipsis: (r) => (r.partnerName ? `${r.memberName} / ${r.partnerName}` : r.memberName),
    },
    {
      key: 'personInChargeName',
      header: '担当者 / 課長',
      width: 160,
      ellipsis: (r) =>
        r.managerName && r.managerName !== r.personInChargeName
          ? `${r.personInChargeName} / ${r.managerName}`
          : r.personInChargeName,
    },
    {
      key: 'submittedAt',
      header: '申請日',
      width: 100,
      render: (r) => (
        <Box component="span" sx={{ color: 'text.secondary' }}>
          {r.submittedAt ? formatDate(r.submittedAt) : '—'}
        </Box>
      ),
    },
    {
      key: 'approved',
      header: '承認',
      width: 140,
      render: (r) => (
        <Box component="span" sx={{ color: 'text.secondary' }}>
          {r.approvedByName ? `${r.approvedByName} ${formatDate(r.approvedAt!)}` : '—'}
        </Box>
      ),
    },
  ];

  // URL直指定（/contract/request/:contractRequestId）の場合は全画面詳細表示
  if (urlContractRequestId) {
    const handleDirectDetailUpdated = () => {
      if (directDetail) {
        getContractRequest({ contractRequestId: directDetail.contractRequestId })
          .then((detail) => setDirectDetail(detail))
          .catch(() => errorDefault('詳細の再取得に失敗しました'));
      }
    };

    if (!directDetail) {
      return (
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}
        >
          <Typography variant="body2" color="text.secondary">
            {isProgress ? '' : '契約申請が見つかりませんでした'}
          </Typography>
        </Box>
      );
    }

    return (
      <ContractRequestDetailView
        detail={directDetail}
        userSession={userSession}
        users={users}
        members={members}
        isBackOffice={isBackOffice}
        onUpdated={handleDirectDetailUpdated}
        onBack={() => window.close()}
      />
    );
  }

  return (
    <Box sx={{ height: '100%', overflow: 'hidden' }}>
      <ConsoleHeader
        title="契約申請"
        count={requests.length}
        description="契約申請の検索・確認・承認/否決を行います"
        backLabel="契約管理"
        onBack={() => navigate('/contract')}
        onRefresh={refetch}
      />

      <ConsoleTable
        columns={columns}
        rows={requests}
        rowKey={(r) => r.contractRequestId}
        selectedKey={selectedDetail?.contractRequestId}
        onRowClick={handleRowClick}
        emptyMessage="契約申請がありません。検索条件を変更してください。"
        toolbar={
          <ConsoleSearchFilter
            conditions={searchConditions}
            initialFilters={initialFilters}
            onFiltersChange={handleSearch}
            conditionKey="contract-request-list"
            userProfileKey="contract-request-list-search"
          />
        }
      />

      {/* 詳細ドロワー（コメント・チェックリストを含む複合ビュー） */}
      <ConsoleDetailDrawer
        open={selectedDetail !== null}
        onClose={() => setSelectedDetail(null)}
        title={selectedDetail?.managementId ?? ''}
        badges={
          selectedDetail && (
            <>
              <RequestTypeChip requestType={selectedDetail.requestType} />
              <RequestStatusChip requestStatus={selectedDetail.requestStatus} />
            </>
          )
        }
        width="min(80vw, 1280px)"
      >
        {selectedDetail && (
          <Box sx={{ height: '100%', mx: -2.5, mt: -2.5 }}>
            <ContractRequestDetailView
              detail={selectedDetail}
              userSession={userSession}
              users={users}
              members={members}
              isBackOffice={isBackOffice}
              onUpdated={handleDetailUpdated}
            />
          </Box>
        )}
      </ConsoleDetailDrawer>

      <Circular open={isProgress} />
    </Box>
  );
};

// --- ContractRequestDetailView ---
type ContractRequestDetailViewProps = {
  detail: ContractRequestDetailResult;
  userSession: UserSession;
  users: UserResult[];
  members: MemberListResult[];
  isBackOffice: boolean;
  onUpdated: (kind: 'status' | 'content') => void;
  /** 指定時、詳細上部に戻るボタンを表示する（ID直開き時はタブを閉じる用途） */
  onBack?: () => void;
};

const ContractRequestDetailView: React.FC<ContractRequestDetailViewProps> = ({
  detail,
  userSession,
  users,
  members,
  isBackOffice,
  onUpdated,
  onBack,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  // 下部タブ。null = 未選択（最小化）。同タブ再選択で最小化、違うタブで最大化。
  const [selectedTab, setSelectedTab] = useState<number | null>(null);
  const expanded = selectedTab !== null;
  const handleTabClick = (index: number) =>
    setSelectedTab((prev) => (prev === index ? null : index));
  const contentTabRef = useRef(0);
  if (selectedTab !== null) contentTabRef.current = selectedTab;
  const contentTab = contentTabRef.current;
  const detailScrollRef = useRef<HTMLDivElement>(null);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {onBack && (
        <Stack
          direction="row"
          alignItems="center"
          sx={{ px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
        >
          <Button
            size="small"
            startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
            onClick={onBack}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            閉じる
          </Button>
        </Stack>
      )}
      {/* 詳細上部（本文 + 右側スクロール連動目次） */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
        {/* 本文スクロール領域: 内部の sticky ヘッダー（アクションボタン）が
            mx:-2/px:2 で全幅ブリードするため、左右に px:2 を確保して整合させる */}
        <Box ref={detailScrollRef} sx={{ flex: 1, minWidth: 0, overflow: 'auto', px: 2, pt: 1 }}>
          <ContractRequestDetail
            detail={detail}
            userSession={userSession}
            users={users}
            members={members}
            isBackOffice={isBackOffice}
            onUpdated={onUpdated}
            onEditModeChange={setIsEditing}
          />
        </Box>
        <FormToc scrollRef={detailScrollRef} deps={[detail.contractRequestId, isEditing]} />
      </Box>

      {/* タブ下部（編集モード時は非表示） */}
      {!isEditing && (
        <Box sx={{ borderTop: 1, borderColor: 'divider', flexShrink: 0 }}>
          <Stack direction="row" alignItems="center" sx={{ px: 2 }}>
            <Tabs value={selectedTab ?? false} sx={{ minHeight: 36, flex: 1 }}>
              <Tab
                label="コメント"
                onClick={() => handleTabClick(0)}
                sx={{ textTransform: 'none', minHeight: 36, py: 0 }}
              />
              <Tab
                label="チェックリスト"
                onClick={() => handleTabClick(1)}
                sx={{ textTransform: 'none', minHeight: 36, py: 0 }}
              />
            </Tabs>
            <IconButton
              size="small"
              aria-label={expanded ? '最小化' : '最大化'}
              onClick={() => setSelectedTab(expanded ? null : 0)}
              sx={{
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
              }}
            >
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Collapse in={expanded} timeout={250} unmountOnExit>
            <Box sx={{ p: 2, height: 360, overflow: 'auto' }}>
              {contentTab === 0 && (
                <CommentSection
                  contractId={detail.contractId}
                  contractRequestId={detail.contractRequestId}
                />
              )}
              {contentTab === 1 && (
                <ChecklistSection
                  contractRequestId={detail.contractRequestId}
                  checklists={detail.checklists}
                  onChecklistUpdate={() => {
                    // チェック更新はドロワーを閉じない
                    onUpdated('status');
                  }}
                  canOperate={isBackOffice}
                  isApproved={detail.requestStatus === 'APPROVED'}
                />
              )}
            </Box>
          </Collapse>
        </Box>
      )}
    </Box>
  );
};

export default ContractRequestManagement;
